/** @jsx isolateComponent */

import type { Interpolation } from '@emotion/css';
import { Colors, ProgressBar, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { abbreviateNumber } from './abbreviateNumber';

export type Props = {
  /**
   * What the `UsageBar` is measuring.
   */
  label: string;
  /**
   * How much can be used.
   */
  max: number;
  /**
   * How much has been used.
   */
  value: number;
};

const Styles = labelStyles('UsageBar', {
  root: {
    display: 'block',
  },
  text: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'auto 1fr',
    marginBottom: 5,
  },
  label: {
    ...Typography.base,
    fontSize: 10,
    lineHeight: '20px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: Colors.grey700,
  },
  amounts: {
    ...Typography.base,
    fontSize: 12,
    lineHeight: '20px',
    fontWeight: 600,
    textAlign: 'right',
    color: Colors.grey500,
  },
});

const formatNumber = (number: number): string => {
  const formatted =
    number > 9999 ? abbreviateNumber(number) : number.toLocaleString();
  return typeof formatted === 'string' ? formatted : 'Error';
};

/**
 * @private
 * Renders a `ProgressBar` for usage with labels.
 */
const UsageBar = (props: Props) => {
  const amount =
    props.max === Infinity
      ? 'Unlimited'
      : `${formatNumber(props.value)} / ${formatNumber(props.max)}`;
  return (
    <figure css={Styles.root}>
      <p
        aria-label={`Used ${props.value} of ${props.max} ${props.label}`}
        css={Styles.text as Interpolation}
      >
        <span aria-hidden={true} css={Styles.label as Interpolation}>
          {props.label}
        </span>
        <span aria-hidden={true} css={Styles.amounts as Interpolation}>
          {amount}
        </span>
      </p>
      {props.max !== Infinity && (
        <ProgressBar max={props.max} value={props.value} />
      )}
    </figure>
  );
};

export default UsageBar;
